import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { queryKeys } from './queryKeys';

export function removeGalleryItemFromPages(data, id) {
  if (!data?.pages || !Array.isArray(data.pages)) return data;

  const normalizedId = String(id);
  let changed = false;
  let totalDelta = 0;

  const nextPages = data.pages.map((page) => {
    const items = Array.isArray(page?.items) ? page.items : [];
    const nextItems = items.filter((item) => String(item?.id) !== normalizedId);
    if (nextItems.length === items.length) return page;

    changed = true;
    totalDelta += items.length - nextItems.length;
    return {
      ...page,
      items: nextItems,
      total: Math.max(0, Number(page?.total || 0) - (items.length - nextItems.length)),
    };
  });

  if (!changed) return data;

  return {
    ...data,
    pages: nextPages,
    pageParams: data.pageParams,
    total: Math.max(0, Number(data?.total || 0) - totalDelta),
  };
}

export function removeGalleryItemFromViewerCache(data, id) {
  if (!data) return data;
  const normalizedId = String(id);
  const nextPhotoItems = Array.isArray(data.photoItems)
    ? data.photoItems.filter((item) => String(item?.id) !== normalizedId)
    : data.photoItems;

  if (String(data.item?.id) === normalizedId) {
    return {
      ...data,
      item: null,
      photoItems: nextPhotoItems,
    };
  }

  if (nextPhotoItems === data.photoItems) return data;
  return {
    ...data,
    photoItems: nextPhotoItems,
  };
}

export function invalidateUserActivity(queryClient) {
  queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
  queryClient.invalidateQueries({ queryKey: ['history'] });
  queryClient.invalidateQueries({ queryKey: ['user-history'] });
  queryClient.invalidateQueries({ queryKey: ['gallery'] });
  queryClient.invalidateQueries({ queryKey: ['gallery-item'] });
}

export function useCreditsInvoiceTelegramMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/credits/invoice/telegram', payload).then((res) => res.data),
  });
}

export function useCreditsCheckoutP4yMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/credits/checkout/p4y', payload).then((res) => res.data),
  });
}

export function useCreditsCheckoutStripeMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/credits/checkout/stripe', payload).then((res) => res.data),
  });
}

export function useSubscriptionInvoiceTelegramMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/subscription/invoice/telegram', payload).then((res) => res.data),
  });
}

export function useSubscriptionCheckoutP4yMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/subscription/checkout/p4y', payload).then((res) => res.data),
  });
}

export function useSubscriptionCheckoutStripeMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/subscription/checkout/stripe', payload).then((res) => res.data),
  });
}

export function useGenerateImageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/generate', payload).then((res) => res.data),
    onSuccess: () => invalidateUserActivity(queryClient),
  });
}

export function useGenerateVideoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/video', payload).then((res) => res.data),
    onSuccess: () => invalidateUserActivity(queryClient),
  });
}

export function usePhotoToVideoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/video/photo-to-video', payload).then((res) => res.data),
    onSuccess: () => invalidateUserActivity(queryClient),
  });
}

export function usePromptHelperMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/prompt-helper', payload).then((res) => res.data),
  });
}

export function useUploadReferenceMutation() {
  return useMutation({
    mutationFn: (formData) =>
      api.post('/upload/reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((res) => res.data),
  });
}

export function useDeleteReferenceMutation() {
  return useMutation({
    mutationFn: (url) => api.delete('/upload/reference', { data: { url } }).then((res) => res.data),
  });
}

export function useDeleteGalleryItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/gallery/${id}`).then((res) => res.data),
    onSuccess: (_result, id) => {
      const galleryKeys = [
        queryKeys.gallery('all'),
        queryKeys.gallery('photo'),
        queryKeys.gallery('video'),
      ];
      for (const key of galleryKeys) {
        queryClient.setQueryData(key, (current) => removeGalleryItemFromPages(current, id));
      }
      queryClient.setQueriesData(
        { queryKey: ['gallery-item'] },
        (current) => removeGalleryItemFromViewerCache(current, id),
      );
      queryClient.invalidateQueries({ queryKey: ['gallery'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-item'] });
      queryClient.invalidateQueries({ queryKey: ['history'] });
      queryClient.invalidateQueries({ queryKey: ['user-history'] });
    },
  });
}

export function usePrepareShareMutation() {
  return useMutation({
    mutationFn: (payload) => api.post('/share/prepared', payload).then((res) => res.data),
  });
}

export function useGalleryDownloadMutation() {
  return useMutation({
    mutationFn: (id) => api.get(`/gallery/${id}/download`).then((res) => res.data),
  });
}
